/**
 * Lightweight i18n dictionaries — kept flat for maintainability.
 * English is the source of truth; missing keys fall back to English.
 * Arabic and French cover the highest-impact copy on Nav, Partners and Products.
 */

export const LANGS = {
  en: { name: "English", short: "EN", dir: "ltr" },
  ar: { name: "العربية", short: "AR", dir: "rtl" },
  fr: { name: "Français", short: "FR", dir: "ltr" },
};

export const dict = {
  en: {
    "nav.services": "Services",
    "nav.products": "Products",
    "nav.erp": "ERP",
    "nav.success": "Success",
    "nav.cases": "Case Studies",
    "nav.insights": "Insights",
    "nav.partners": "Partners",
    "nav.careers": "Careers",
    "nav.admin": "Admin login",
    "nav.dashboard": "Dashboard",
    "nav.talk": "Talk to us",
    "nav.menu": "MENU",

    "cta.quote": "Get a quote",
    "cta.demo": "Request a demo",
    "cta.pricing": "Get pricing",
    "cta.consult": "Schedule a free consultation",
    "cta.pilot": "Start a pilot",
    "cta.whatsapp": "Chat on WhatsApp",

    // Partners
    "partners.seo.title": "Partner program — reseller, white-label & distributor",
    "partners.seo.desc": "Grow with Maxine — 10–55% revenue share across referral, reseller / white-label and regional distributor tracks. Territories open across Africa, GCC and Europe.",
    "partners.eyebrow": "Partner program",
    "partners.hero.title.a": "Sell Maxine ERP",
    "partners.hero.title.b": "in your ",
    "partners.hero.title.c": "territory",
    "partners.hero.sub": "Refer a deal, white-label the product, or take exclusive rights to a country. Three tracks, revenue share up to 55%, and territories currently open across Africa, GCC and Europe.",
    "partners.hero.apply": "Apply now",
    "partners.hero.ask": "Ask a question",
    "partners.territories.eyebrow": "Territories currently open",
    "partners.territories.note": "Not seeing your country? We take one exclusive distributor per territory — apply below and we'll open a conversation.",
    "partners.tracks.eyebrow": "Three ways to partner",
    "partners.tracks.title": "Pick the track that matches your business.",
    "partners.tracks.note": "Every track ships with a deal-registration portal, sandbox access and named partner manager. No forced monthly quotas — you scale at your pace.",
    "partners.track1.title": "Referral partner",
    "partners.track1.tagline": "Send us qualified deals, take a share",
    "partners.track1.period": "of first-year contract value",
    "partners.track1.ideal": "Consultants, boutique agencies, freelance CIOs",
    "partners.track2.title": "Reseller / white-label",
    "partners.track2.tagline": "Sell Maxine ERP under your brand",
    "partners.track2.period": "gross margin",
    "partners.track2.ideal": "System integrators & MSPs with a book of business",
    "partners.track3.title": "Regional distributor",
    "partners.track3.tagline": "Exclusive country / region rights",
    "partners.track3.period": "gross margin + territory rights",
    "partners.track3.ideal": "Established software distributors in Africa, GCC or EU",
    "partners.bestfor": "Best for",
    "partners.steps.eyebrow": "How onboarding works",
    "partners.steps.title": "Five steps, two weeks, first deal registered.",
    "partners.steps.note": "We aim to get every new partner past step five within 14 calendar days — no procurement theatre, no long legal cycles.",
    "partners.form.eyebrow": "Application",
    "partners.form.title": "Start the conversation.",
    "partners.form.sub": "Fill this in — a partner manager replies within 2 business days with an NDA and a discovery-call slot. No spam, no drip sequence.",
    "partners.form.name": "Your name *",
    "partners.form.email": "Work email *",
    "partners.form.company": "Company *",
    "partners.form.country": "Country / region",
    "partners.form.phone": "WhatsApp / phone",
    "partners.form.track": "Track you're interested in *",
    "partners.form.msg": "Tell us about your business",
    "partners.form.msg.ph": "Which industries / clients do you serve? Any live pipeline you'd like to co-sell?",
    "partners.form.submit": "Submit application",
    "partners.form.sending": "Sending…",
    "partners.form.sla": "Reply within 2 business days",
    "partners.form.success": "Application received — a partner manager will reply within 2 business days.",
    "partners.form.error": "Name, work email and company are required",

    // Product detail
    "product.back": "All products",
    "product.inside": "What's inside",
    "product.pricegate": "Pricing revealed after a quick 20-min demo",
    "product.host": "Deployed in production across UAE, India and EU. SLA-backed hosting available in your data region.",
    "product.ready": "Ready to move?",
    "product.readyTitle": "Let's scope your {product} pilot this week.",
    "product.readySub": "First call is 20 minutes and free — you leave with a scope, a fixed price and a timeline you can share with your board.",
    "product.tour": "Take the interactive tour",

    // Comparison
    "cmp.eyebrow": "How we compare",
    "cmp.title": "{product} vs. traditional global ERP suites",
    "cmp.sub": "A short, honest side-by-side. Traditional suites still win at Fortune-500 breadth — we win at go-live speed, regional compliance and total cost for teams of 20 to 2,000.",
    "cmp.col.criterion": "Criterion",
    "cmp.col.us": "Maxine ERP",
    "cmp.col.them": "Traditional global suite",

    // ROI
    "roi.eyebrow": "ROI mini-calculator",
    "roi.title": "Estimate savings from {product}",
    "roi.team": "Team size using the tool",
    "roi.tool": "Current monthly tool spend",
    "roi.hours": "Hours saved per person / week",
    "roi.labour": "Monthly labour saved",
    "roi.tools": "Monthly tool saving",
    "roi.total": "Estimated monthly savings",
    "roi.year": "≈ {annual} / year · payback in ~{months} month{s}",
    "roi.send": "Send this to sales",
    "roi.pilot": "Scope a pilot",
    "roi.disclaimer": "Uses conservative local labour rate · {region} · US${rate}/hr · assumes 60% legacy-tool replacement",

    // Hero
    "hero.eyebrow": "Maxine Consulting — est. 2016 · ERP · Web · Cloud",
    "hero.title.a": "Regional ERP",
    "hero.title.b": "that ",
    "hero.title.c": "actually ships",
    "hero.sub": "Twelve industry-tuned ERP suites for manufacturing, healthcare, retail, logistics and hospitality — plus the websites, cloud and marketing to launch them. Priced for teams of 20 to 2,000, live in 30 days.",
    "hero.quote": "Get a fixed quote",
    "hero.packages": "See packages",
    "hero.stat.clients": "clients shipped",
    "hero.stat.years": "on the ground",
    "hero.stat.hubs": "delivery hubs",

    // Global trust strip
    "trust.years": "shipping software",
    "trust.countries": "countries served",
    "trust.impl": "implementations",
    "trust.support": "support desk",
    "trust.iso": "compliance-ready",

    // ERP leadership
    "erp.eyebrow": "ERP · Our core practice",
    "erp.title": "The regional ERP suite for manufacturing & growing businesses.",
    "erp.sub": "While SAP and Oracle target the Fortune 500, we build the same modules — finance, MRP, inventory, HR, payroll, sales — sized and priced for teams of 20 to 2,000. Twelve industry-tuned ERP products already in production, deployed in weeks, not years.",
    "erp.seeProducts": "See ERP products",
    "erp.proposal": "Get an ERP proposal",
    "erp.mod.mfg": "Manufacturing & MRP",
    "erp.mod.inv": "Inventory & WMS",
    "erp.mod.fin": "Finance & GST/VAT",
    "erp.mod.hr": "HR & Payroll · WPS",
    "erp.mod.ps": "Purchase & Sales",
    "erp.mod.bi": "BI & Reports",
    "erp.proof.golive": "Typical ERP go-live vs. 12–18 months on global suites",
    "erp.proof.region": "Regional data residency and full VAT / GST / e-invoice compliance",
    "erp.proof.clients": "Manufacturing, healthcare, retail, logistics & hospitality live in production",

    // Why Maxine
    "why.eyebrow": "Why Maxine",
    "why.title": "A consultancy that behaves like a product team.",
    "why.sub": "Our four principles are non-negotiable. They come from nine years of ambitious founders and CIOs — and a few lessons we would rather forget.",
    "why.v1.title": "Fixed timelines, no surprises",
    "why.v1.desc": "Every engagement ships in a fixed window — no open-ended hourly bills.",
    "why.v2.title": "Data residency you control",
    "why.v2.desc": "Choose UAE, EU or India for hosting. Contracts specify exactly where data lives.",
    "why.v3.title": "One partner, one contract",
    "why.v3.desc": "Product, marketing, cloud and support — all under one MSA and one PM.",
    "why.v4.title": "Growth metrics on record",
    "why.v4.desc": "Every project ships with a metrics dashboard; we grade ourselves in public.",

    // Process
    "process.eyebrow": "How we work",
    "process.title": "A rigorous, repeatable five-step delivery process.",
    "process.s1.t": "Discovery",
    "process.s1.d": "60-min working session to nail scope, KPIs and constraints.",
    "process.s2.t": "Blueprint",
    "process.s2.d": "Fixed-price proposal with milestones, deliverables and rollback plan.",
    "process.s3.t": "Sprint build",
    "process.s3.d": "Weekly demos, transparent PM tool, direct Slack access to the team.",
    "process.s4.t": "Launch",
    "process.s4.d": "Deploy, migrate, train. Then we sit next to you for 30 days.",
    "process.s5.t": "Grow",
    "process.s5.d": "Optional retainer for marketing, cloud ops and product iteration.",

    // Success Stories
    "success.eyebrow": "Global success stories",
    "success.title": "Live in production across three continents.",
    "success.sub": "Six representative rollouts — from Lagos logistics to Bengaluru academies — showing the pattern: problem → tailored ERP → measurable outcome.",
    "success.problem": "Problem",
    "success.solution": "Solution",
    "success.result": "Result",
    "success.disclaimer": "Client names withheld under NDA · full references shared on request during a demo call.",
    "success.ind.logistics": "Logistics",
    "success.ind.retail": "Retail",
    "success.ind.manufacturing": "Manufacturing",
    "success.ind.banking": "Banking",
    "success.ind.healthcare": "Healthcare",
    "success.ind.education": "Education",
    "success.ng.p": "A cross-border trucking operator lost 6% of shipments a month to paper way-bills and driver phone-book routing.",
    "success.ng.s": "Deployed Fleet & Logistics ERP with GPS reconciliation, WhatsApp POD capture and multi-warehouse WMS.",
    "success.ng.r": "on-time delivery ↑ 78 → 96%",
    "success.bw.p": "12-outlet FMCG chain was closing books 22 days into every new month with hand-keyed stock counts.",
    "success.bw.s": "Restaurant / Retail POS + inventory suite with barcode receiving and nightly SKU-level P&L.",
    "success.bw.r": "month-close 22 → 4 days",
    "success.zm.p": "Copper-belt manufacturer running MRP on shared Excel sheets — 18% raw-material wastage on rework.",
    "success.zm.s": "Manufacturing ERP with routings, MRP, quality gates and e-invoice VAT compliance.",
    "success.zm.r": "wastage 18 → 6%",
    "success.zw.p": "A private bank was reconciling three currencies against nostro statements by hand — 3-day cycle, weekly break-fixes.",
    "success.zw.s": "Bank Reconciliation Suite auto-matching Nostro, EFT, cards and clearing.",
    "success.zw.r": "recon cycle 3 days → 4 hours",
    "success.ae.p": "12-branch clinic group with 28% no-show rate and 45-day TPA settlement cycle.",
    "success.ae.s": "Hospital / Clinic HMS with two-step reminders, TPA workspace and pharmacy tie-in.",
    "success.ae.r": "no-shows 28 → 18%",
    "success.in.p": "K-12 academy chain lost 40% parent app engagement after a legacy vendor deprecated their app.",
    "success.in.s": "School & College ERP with a native parent app, live bus tracking and fee gateway.",
    "success.in.r": "parent DAU ↑ 3.1×",

    // Contact
    "contact.eyebrow": "Contact",
    "contact.title": "Schedule a free consultation.",
    "contact.sub": "A senior partner replies within one business day. Ask for a demo, request pricing, or scope a pilot — first 20 minutes are on us.",
    "contact.b1": "Request a demo",
    "contact.b2": "Get pricing",
    "contact.b3": "Start a pilot",
    "contact.b4": "Become a partner",
    "contact.whatsapp": "WhatsApp",
    "contact.email": "Email",
    "contact.office": "Head office",
    "contact.form.name": "Your name",
    "contact.form.email": "Email",
    "contact.form.phone": "Phone (optional)",
    "contact.form.company": "Company",
    "contact.form.msg": "What are you building?",
    "contact.form.msg.ph": "A one-liner about your project…",
    "contact.form.wa": "Also open WhatsApp after submitting",
    "contact.form.submit": "Send message",
    "contact.form.sending": "Sending…",
    "contact.form.chat": "Chat on WhatsApp",
    "contact.toast.error": "Name, email and message are required",
    "contact.toast.success": "Thanks — we'll be in touch within 1 business day.",
  },

  ar: {
    "nav.services": "الخدمات",
    "nav.products": "المنتجات",
    "nav.erp": "تخطيط الموارد",
    "nav.success": "قصص النجاح",
    "nav.cases": "دراسات الحالة",
    "nav.insights": "مقالات",
    "nav.partners": "الشركاء",
    "nav.careers": "الوظائف",
    "nav.admin": "دخول المسؤول",
    "nav.dashboard": "لوحة التحكم",
    "nav.talk": "تواصل معنا",
    "nav.menu": "القائمة",

    "cta.quote": "احصل على عرض سعر",
    "cta.demo": "اطلب عرضًا تجريبيًا",
    "cta.pricing": "احصل على السعر",
    "cta.consult": "جدولة استشارة مجانية",
    "cta.pilot": "ابدأ مشروعًا تجريبيًا",
    "cta.whatsapp": "الدردشة عبر واتساب",

    "partners.seo.title": "برنامج الشركاء — إعادة بيع، علامة بيضاء، وموزع",
    "partners.seo.desc": "انمُ مع Maxine — حصة من الإيرادات من 10 إلى 55٪ عبر مسارات الإحالة وإعادة البيع والتوزيع الإقليمي. المناطق مفتوحة عبر إفريقيا والخليج وأوروبا.",
    "partners.eyebrow": "برنامج الشركاء",
    "partners.hero.title.a": "بِع نظام Maxine ERP",
    "partners.hero.title.b": "في ",
    "partners.hero.title.c": "منطقتك",
    "partners.hero.sub": "أحِل صفقة، أو بِع المنتج باسم علامتك، أو خذ حقوقًا حصرية لدولة كاملة. ثلاثة مسارات، حصة إيرادات تصل إلى 55٪، ومناطق مفتوحة الآن في إفريقيا والخليج وأوروبا.",
    "partners.hero.apply": "قدّم الآن",
    "partners.hero.ask": "اسأل سؤالاً",
    "partners.territories.eyebrow": "المناطق المفتوحة حاليًا",
    "partners.territories.note": "لا ترى بلدك؟ لدينا موزّع حصري واحد لكل منطقة — قدّم أدناه وسنبدأ الحوار.",
    "partners.tracks.eyebrow": "ثلاث طرق للشراكة",
    "partners.tracks.title": "اختر المسار المناسب لعملك.",
    "partners.tracks.note": "كل مسار مزوّد ببوابة تسجيل صفقات، وحساب تجريبي، ومدير شركاء مخصص. لا حصص شهرية إجبارية.",
    "partners.track1.title": "شريك إحالة",
    "partners.track1.tagline": "أرسل لنا صفقات مؤهلة، خذ حصة",
    "partners.track1.period": "من قيمة العقد للسنة الأولى",
    "partners.track1.ideal": "مستشارون، وكالات صغيرة، مسؤولو تقنية معلومات مستقلون",
    "partners.track2.title": "بائع / علامة بيضاء",
    "partners.track2.tagline": "بِع Maxine ERP باسم علامتك",
    "partners.track2.period": "هامش إجمالي",
    "partners.track2.ideal": "متكاملو أنظمة ومزودو خدمات مُدارة",
    "partners.track3.title": "موزّع إقليمي",
    "partners.track3.tagline": "حقوق حصرية للدولة / المنطقة",
    "partners.track3.period": "هامش إجمالي + حقوق إقليمية",
    "partners.track3.ideal": "موزعو برمجيات في إفريقيا أو الخليج أو أوروبا",
    "partners.bestfor": "الأنسب لـ",
    "partners.steps.eyebrow": "طريقة الانطلاق",
    "partners.steps.title": "خمس خطوات، أسبوعان، أول صفقة مسجّلة.",
    "partners.steps.note": "نستهدف تجاوز الخطوة الخامسة خلال 14 يومًا لكل شريك جديد — بلا بيروقراطية.",
    "partners.form.eyebrow": "التقديم",
    "partners.form.title": "لنبدأ الحوار.",
    "partners.form.sub": "املأ النموذج — سيردّ مدير الشركاء خلال يومي عمل مع اتفاقية عدم إفصاح وموعد للمكالمة الاستكشافية.",
    "partners.form.name": "الاسم *",
    "partners.form.email": "البريد الإلكتروني للعمل *",
    "partners.form.company": "الشركة *",
    "partners.form.country": "الدولة / المنطقة",
    "partners.form.phone": "واتساب / هاتف",
    "partners.form.track": "المسار الذي يهمك *",
    "partners.form.msg": "أخبرنا عن عملك",
    "partners.form.msg.ph": "ما القطاعات / العملاء الذين تخدمهم؟ هل لديك صفقات فعلية للتعاون فيها؟",
    "partners.form.submit": "إرسال الطلب",
    "partners.form.sending": "جارٍ الإرسال…",
    "partners.form.sla": "الرد خلال يومي عمل",
    "partners.form.success": "تم استلام طلبك — سيتواصل معك مدير الشركاء خلال يومي عمل.",
    "partners.form.error": "الاسم والبريد والشركة مطلوبة",

    "product.back": "كل المنتجات",
    "product.inside": "المميزات",
    "product.pricegate": "يُكشف السعر بعد عرض تجريبي قصير (20 دقيقة)",
    "product.host": "منشور في الإنتاج عبر الإمارات والهند وأوروبا. استضافة باتفاقية مستوى خدمة في منطقتك.",
    "product.ready": "جاهز للانطلاق؟",
    "product.readyTitle": "لنحدّد نطاق نشر {product} خلال أسبوع.",
    "product.readySub": "المكالمة الأولى 20 دقيقة ومجانية — تخرج منها بخطة نطاق، وسعر ثابت، وجدول زمني تشاركه مع مجلس الإدارة.",
    "product.tour": "خذ الجولة التفاعلية",

    "cmp.eyebrow": "كيف نتفوّق",
    "cmp.title": "{product} مقابل حزم تخطيط الموارد التقليدية",
    "cmp.sub": "مقارنة قصيرة وصادقة. الحزم العالمية تتفوّق في اتساع Fortune 500 — ونتفوّق نحن في سرعة الانطلاق والامتثال الإقليمي والتكلفة الإجمالية لفرق من 20 إلى 2000 موظف.",
    "cmp.col.criterion": "المعيار",
    "cmp.col.us": "Maxine ERP",
    "cmp.col.them": "الحزم العالمية التقليدية",

    "roi.eyebrow": "حاسبة العائد",
    "roi.title": "قدّر التوفير من {product}",
    "roi.team": "حجم الفريق المستخدم",
    "roi.tool": "الإنفاق الشهري الحالي على الأدوات",
    "roi.hours": "الساعات الموفّرة لكل شخص / أسبوع",
    "roi.labour": "توفير العمالة الشهري",
    "roi.tools": "توفير الأدوات الشهري",
    "roi.total": "إجمالي التوفير الشهري",
    "roi.year": "≈ {annual} / السنة · استرداد رأس المال خلال {months} شهر",
    "roi.send": "أرسل هذا للمبيعات",
    "roi.pilot": "خطط مشروعًا تجريبيًا",
    "roi.disclaimer": "أجر عمالة محلي متحفّظ · {region} · {rate}$/ساعة · بافتراض استبدال 60٪ من الأداة القديمة",

    "hero.eyebrow": "Maxine Consulting — تأسست 2016 · ERP · ويب · سحابة",
    "hero.title.a": "نظام ERP إقليمي",
    "hero.title.b": "يُطلق ",
    "hero.title.c": "فعلاً",
    "hero.sub": "اثنا عشر نظام تخطيط موارد جاهز للتصنيع والصحة والتجزئة والخدمات اللوجستية والضيافة — بالإضافة إلى مواقع الويب والسحابة والتسويق لإطلاقها. مسعّرة لفرق من 20 إلى 2000 موظف، وتعمل خلال 30 يومًا.",
    "hero.quote": "احصل على عرض ثابت",
    "hero.packages": "شاهد الباقات",
    "hero.stat.clients": "عملاء",
    "hero.stat.years": "على أرض الواقع",
    "hero.stat.hubs": "مراكز تسليم",

    "trust.years": "سنوات في البرمجيات",
    "trust.countries": "دول مخدومة",
    "trust.impl": "عمليات نشر",
    "trust.support": "دعم فني",
    "trust.iso": "جاهز للامتثال",

    "erp.eyebrow": "تخطيط الموارد · تخصصنا الأساسي",
    "erp.title": "نظام ERP الإقليمي للتصنيع والشركات النامية.",
    "erp.sub": "بينما تستهدف SAP وOracle شركات Fortune 500، نحن نبني الوحدات نفسها — المحاسبة، MRP، المخزون، الموارد البشرية، الرواتب، المبيعات — بحجم وسعر يناسب فرقًا من 20 إلى 2000 موظف. اثنا عشر منتج ERP جاهز للتصنيع في الإنتاج، منشور خلال أسابيع لا سنوات.",
    "erp.seeProducts": "شاهد منتجات ERP",
    "erp.proposal": "احصل على عرض ERP",
    "erp.mod.mfg": "التصنيع وMRP",
    "erp.mod.inv": "المخزون وWMS",
    "erp.mod.fin": "المحاسبة وضريبة القيمة",
    "erp.mod.hr": "الموارد البشرية والرواتب · WPS",
    "erp.mod.ps": "المشتريات والمبيعات",
    "erp.mod.bi": "التقارير الذكية",
    "erp.proof.golive": "متوسط الانطلاق مقابل 12–18 شهرًا للحزم العالمية",
    "erp.proof.region": "استضافة إقليمية للبيانات وامتثال كامل لضريبة القيمة والفوترة الإلكترونية",
    "erp.proof.clients": "تصنيع، صحة، تجزئة، لوجستيات، ضيافة — كلها في الإنتاج",

    "why.eyebrow": "لماذا Maxine",
    "why.title": "استشارات تعمل كفريق منتَج.",
    "why.sub": "مبادئنا الأربعة غير قابلة للتفاوض — خلاصة تسع سنوات مع مؤسسين طموحين ومدراء تقنية معلومات، وبعض الدروس التي نتمنى نسيانها.",
    "why.v1.title": "مواعيد ثابتة، بلا مفاجآت",
    "why.v1.desc": "كل مشروع يُسلَّم في نافذة زمنية محددة — لا فواتير بالساعة مفتوحة.",
    "why.v2.title": "إقامة البيانات بيدك",
    "why.v2.desc": "اختر الإمارات أو أوروبا أو الهند للاستضافة. العقد يحدد بالضبط أين تعيش البيانات.",
    "why.v3.title": "شريك واحد، عقد واحد",
    "why.v3.desc": "المنتج والتسويق والسحابة والدعم — كلها تحت اتفاقية رئيسية واحدة ومدير مشروع واحد.",
    "why.v4.title": "قياسات النمو موثقة",
    "why.v4.desc": "كل مشروع يُشحن بلوحة معلومات نتائج — نُقيّم أنفسنا علنًا.",

    "process.eyebrow": "طريقة عملنا",
    "process.title": "عملية تسليم صارمة قابلة للتكرار من خمس خطوات.",
    "process.s1.t": "الاستكشاف",
    "process.s1.d": "جلسة عمل مدتها 60 دقيقة لتحديد النطاق ومؤشرات الأداء والقيود.",
    "process.s2.t": "المخطط",
    "process.s2.d": "عرض بسعر ثابت مع مراحل تسليم واضحة وخطة تراجع.",
    "process.s3.t": "بناء متسارع",
    "process.s3.d": "عروض أسبوعية، أداة إدارة مشاريع شفافة، وصول مباشر إلى الفريق عبر Slack.",
    "process.s4.t": "الإطلاق",
    "process.s4.d": "نشر، ترحيل بيانات، تدريب — ثم نجلس بجانبك لمدة 30 يومًا.",
    "process.s5.t": "النمو",
    "process.s5.d": "عقد صيانة اختياري للتسويق وعمليات السحابة وتطوير المنتج.",

    "success.eyebrow": "قصص نجاح عالمية",
    "success.title": "في الإنتاج عبر ثلاث قارات.",
    "success.sub": "ستة تطبيقات تمثيلية — من لاجوس إلى بنغالورو — تُظهر النمط ذاته: مشكلة → ERP مُخصّص → نتيجة قابلة للقياس.",
    "success.problem": "المشكلة",
    "success.solution": "الحل",
    "success.result": "النتيجة",
    "success.disclaimer": "أسماء العملاء محفوظة بموجب اتفاقية عدم إفصاح · نشارك المراجع كاملة عند الطلب في مكالمة العرض التجريبي.",
    "success.ind.logistics": "لوجستيات",
    "success.ind.retail": "تجزئة",
    "success.ind.manufacturing": "تصنيع",
    "success.ind.banking": "بنوك",
    "success.ind.healthcare": "صحة",
    "success.ind.education": "تعليم",
    "success.ng.p": "شركة نقل عابر للحدود كانت تخسر 6٪ من الشحنات شهريًا بسبب أوراق التسليم والتخطيط اليدوي.",
    "success.ng.s": "نشرنا نظام Fleet & Logistics ERP مع تتبع GPS، والتقاط إثباتات التسليم عبر واتساب، ومستودعات متعددة.",
    "success.ng.r": "التسليم في الموعد 78 → 96٪",
    "success.bw.p": "سلسلة تجزئة من 12 فرعًا كانت تُغلق حساباتها بعد 22 يومًا من الشهر الجديد بجرد يدوي.",
    "success.bw.s": "نظام POS للتجزئة مع مخزون بالباركود وتقارير ربحية يومية بمستوى المنتج.",
    "success.bw.r": "إغلاق الشهر 22 → 4 أيام",
    "success.zm.p": "شركة تصنيع كانت تدير MRP في ملفات Excel مشتركة — 18٪ هدر في المواد الخام.",
    "success.zm.s": "نظام Manufacturing ERP مع مسارات إنتاج، MRP، بوابات جودة، وامتثال الفاتورة الإلكترونية.",
    "success.zm.r": "الهدر 18 → 6٪",
    "success.zw.p": "بنك خاص كان يوفّق ثلاث عملات مقابل كشوف Nostro يدويًا — دورة 3 أيام وإصلاحات أسبوعية.",
    "success.zw.s": "نظام Bank Reconciliation يوفّق Nostro و EFT والبطاقات والمقاصة تلقائيًا.",
    "success.zw.r": "دورة التسوية 3 أيام → 4 ساعات",
    "success.ae.p": "مجموعة عيادات من 12 فرعًا بنسبة تخلّف 28٪ ودورة تسوية تأمين 45 يومًا.",
    "success.ae.s": "نظام HMS مع تذكيرات ذكية، وحدة تأمين متكاملة، وربط بالصيدلية.",
    "success.ae.r": "التخلّف 28 → 18٪",
    "success.in.p": "سلسلة أكاديميات فقدت 40٪ من تفاعل تطبيق ولي الأمر بعد توقف مورّد قديم.",
    "success.in.s": "نظام School & College ERP مع تطبيق ولي أمر أصلي، تتبع الحافلات، وبوابة رسوم.",
    "success.in.r": "المستخدمون النشطون يوميًا × 3.1",

    "contact.eyebrow": "تواصل",
    "contact.title": "احجز استشارة مجانية.",
    "contact.sub": "شريك أول يرد خلال يوم عمل واحد. اطلب عرضًا تجريبيًا، أو السعر، أو نطاق مشروع تجريبي — أول 20 دقيقة على حسابنا.",
    "contact.b1": "اطلب عرضًا تجريبيًا",
    "contact.b2": "احصل على السعر",
    "contact.b3": "ابدأ مشروعًا تجريبيًا",
    "contact.b4": "كن شريكًا",
    "contact.whatsapp": "واتساب",
    "contact.email": "البريد الإلكتروني",
    "contact.office": "المكتب الرئيسي",
    "contact.form.name": "اسمك",
    "contact.form.email": "البريد الإلكتروني",
    "contact.form.phone": "هاتف (اختياري)",
    "contact.form.company": "الشركة",
    "contact.form.msg": "ما الذي تبنيه؟",
    "contact.form.msg.ph": "سطر واحد عن مشروعك…",
    "contact.form.wa": "افتح واتساب أيضًا بعد الإرسال",
    "contact.form.submit": "إرسال الرسالة",
    "contact.form.sending": "جارٍ الإرسال…",
    "contact.form.chat": "الدردشة عبر واتساب",
    "contact.toast.error": "الاسم والبريد والرسالة مطلوبة",
    "contact.toast.success": "شكرًا — سنتواصل معك خلال يوم عمل واحد.",
  },

  fr: {
    "nav.services": "Services",
    "nav.products": "Produits",
    "nav.erp": "ERP",
    "nav.success": "Succès",
    "nav.cases": "Études de cas",
    "nav.insights": "Analyses",
    "nav.partners": "Partenaires",
    "nav.careers": "Carrières",
    "nav.admin": "Connexion admin",
    "nav.dashboard": "Tableau de bord",
    "nav.talk": "Parlons-en",
    "nav.menu": "MENU",

    "cta.quote": "Obtenir un devis",
    "cta.demo": "Demander une démo",
    "cta.pricing": "Voir le tarif",
    "cta.consult": "Consultation gratuite",
    "cta.pilot": "Lancer un pilote",
    "cta.whatsapp": "Discuter sur WhatsApp",

    "partners.seo.title": "Programme partenaires — revendeur, marque blanche et distributeur",
    "partners.seo.desc": "Grandissez avec Maxine — 10 à 55 % de partage de revenus sur les pistes référent, revendeur / marque blanche et distributeur régional. Territoires ouverts en Afrique, au GCC et en Europe.",
    "partners.eyebrow": "Programme partenaires",
    "partners.hero.title.a": "Vendez Maxine ERP",
    "partners.hero.title.b": "dans votre ",
    "partners.hero.title.c": "territoire",
    "partners.hero.sub": "Référez une affaire, revendez le produit en marque blanche, ou prenez les droits exclusifs sur un pays. Trois pistes, jusqu'à 55 % de partage de revenus, et des territoires ouverts en Afrique, au GCC et en Europe.",
    "partners.hero.apply": "Postuler",
    "partners.hero.ask": "Poser une question",
    "partners.territories.eyebrow": "Territoires actuellement ouverts",
    "partners.territories.note": "Votre pays n'apparaît pas ? Nous acceptons un seul distributeur exclusif par territoire — postulez ci-dessous et nous ouvrirons la discussion.",
    "partners.tracks.eyebrow": "Trois façons de devenir partenaire",
    "partners.tracks.title": "Choisissez la piste qui correspond à votre activité.",
    "partners.tracks.note": "Chaque piste inclut un portail d'enregistrement des affaires, un accès sandbox et un partner manager dédié. Pas de quotas mensuels forcés.",
    "partners.track1.title": "Partenaire référent",
    "partners.track1.tagline": "Apportez des affaires qualifiées, prenez une part",
    "partners.track1.period": "de la valeur contractuelle de la 1re année",
    "partners.track1.ideal": "Consultants, agences boutique, CIO indépendants",
    "partners.track2.title": "Revendeur / marque blanche",
    "partners.track2.tagline": "Vendez Maxine ERP sous votre marque",
    "partners.track2.period": "marge brute",
    "partners.track2.ideal": "Intégrateurs de systèmes et MSP avec un portefeuille clients",
    "partners.track3.title": "Distributeur régional",
    "partners.track3.tagline": "Droits exclusifs pays / région",
    "partners.track3.period": "marge brute + droits de territoire",
    "partners.track3.ideal": "Distributeurs de logiciels établis en Afrique, GCC ou UE",
    "partners.bestfor": "Idéal pour",
    "partners.steps.eyebrow": "Onboarding",
    "partners.steps.title": "Cinq étapes, deux semaines, première affaire enregistrée.",
    "partners.steps.note": "Nous visons à faire passer chaque nouveau partenaire l'étape cinq en 14 jours calendaires — sans théâtre juridique.",
    "partners.form.eyebrow": "Candidature",
    "partners.form.title": "Commençons la conversation.",
    "partners.form.sub": "Remplissez ce formulaire — un partner manager répond sous 2 jours ouvrés avec un NDA et un créneau pour un appel de découverte.",
    "partners.form.name": "Votre nom *",
    "partners.form.email": "E-mail professionnel *",
    "partners.form.company": "Entreprise *",
    "partners.form.country": "Pays / région",
    "partners.form.phone": "WhatsApp / téléphone",
    "partners.form.track": "Piste qui vous intéresse *",
    "partners.form.msg": "Parlez-nous de votre activité",
    "partners.form.msg.ph": "Quels secteurs / clients servez-vous ? Un pipeline en cours à co-vendre ?",
    "partners.form.submit": "Envoyer la candidature",
    "partners.form.sending": "Envoi…",
    "partners.form.sla": "Réponse sous 2 jours ouvrés",
    "partners.form.success": "Candidature reçue — un partner manager répondra sous 2 jours ouvrés.",
    "partners.form.error": "Nom, e-mail professionnel et entreprise obligatoires",

    "product.back": "Tous les produits",
    "product.inside": "Ce qui est inclus",
    "product.pricegate": "Tarif dévoilé après une démo rapide de 20 min",
    "product.host": "Déployé en production aux Émirats, en Inde et en UE. Hébergement SLA disponible dans votre région.",
    "product.ready": "Prêt à démarrer ?",
    "product.readyTitle": "Cadrons votre pilote {product} cette semaine.",
    "product.readySub": "Premier appel de 20 min offert — vous repartez avec un périmètre, un prix fixe et un calendrier à partager avec votre direction.",
    "product.tour": "Visite interactive",

    "cmp.eyebrow": "Comparaison",
    "cmp.title": "{product} vs. les suites ERP mondiales classiques",
    "cmp.sub": "Un côte-à-côte court et honnête. Les suites classiques gagnent sur la largeur Fortune 500 — nous gagnons sur la vitesse de mise en ligne, la conformité régionale et le coût total pour les équipes de 20 à 2 000 personnes.",
    "cmp.col.criterion": "Critère",
    "cmp.col.us": "Maxine ERP",
    "cmp.col.them": "Suite ERP mondiale classique",

    "roi.eyebrow": "Calculateur ROI",
    "roi.title": "Estimez les économies avec {product}",
    "roi.team": "Taille de l'équipe utilisatrice",
    "roi.tool": "Dépense mensuelle actuelle en outils",
    "roi.hours": "Heures économisées / personne / semaine",
    "roi.labour": "Économies de main-d'œuvre / mois",
    "roi.tools": "Économies d'outils / mois",
    "roi.total": "Économies mensuelles estimées",
    "roi.year": "≈ {annual} / an · retour sur investissement en ~{months} mois",
    "roi.send": "Envoyer aux ventes",
    "roi.pilot": "Cadrer un pilote",
    "roi.disclaimer": "Taux horaire local conservateur · {region} · {rate} $US/h · suppose 60 % de remplacement de l'outil hérité",

    "hero.eyebrow": "Maxine Consulting — depuis 2016 · ERP · Web · Cloud",
    "hero.title.a": "L'ERP régional",
    "hero.title.b": "qui ",
    "hero.title.c": "livre vraiment",
    "hero.sub": "Douze suites ERP taillées pour l'industrie, la santé, le commerce, la logistique et l'hôtellerie — plus les sites, le cloud et le marketing pour les lancer. Tarifées pour les équipes de 20 à 2 000, en production sous 30 jours.",
    "hero.quote": "Devis à prix fixe",
    "hero.packages": "Voir les offres",
    "hero.stat.clients": "clients livrés",
    "hero.stat.years": "sur le terrain",
    "hero.stat.hubs": "hubs de livraison",

    "trust.years": "d'édition logicielle",
    "trust.countries": "pays servis",
    "trust.impl": "déploiements",
    "trust.support": "support",
    "trust.iso": "prêt pour la conformité",

    "erp.eyebrow": "ERP · Notre cœur de métier",
    "erp.title": "La suite ERP régionale pour l'industrie et les PME.",
    "erp.sub": "Là où SAP et Oracle visent le Fortune 500, nous construisons les mêmes modules — finance, MRP, stock, RH, paie, ventes — dimensionnés et tarifés pour les équipes de 20 à 2 000. Douze produits ERP sectoriels déjà en production, déployés en semaines, pas en années.",
    "erp.seeProducts": "Voir les produits ERP",
    "erp.proposal": "Demander une proposition ERP",
    "erp.mod.mfg": "Industrie & MRP",
    "erp.mod.inv": "Stock & WMS",
    "erp.mod.fin": "Finance & TVA / GST",
    "erp.mod.hr": "RH & Paie · WPS",
    "erp.mod.ps": "Achats & Ventes",
    "erp.mod.bi": "BI & Rapports",
    "erp.proof.golive": "Mise en production type vs. 12–18 mois pour les suites mondiales",
    "erp.proof.region": "Résidence des données régionale et conformité TVA / GST / e-facture complète",
    "erp.proof.clients": "Industrie, santé, retail, logistique et hôtellerie en production",

    "why.eyebrow": "Pourquoi Maxine",
    "why.title": "Un cabinet qui pense comme une équipe produit.",
    "why.sub": "Nos quatre principes ne sont pas négociables. Neuf ans de fondateurs ambitieux et de DSI — plus quelques leçons qu'on préférerait oublier.",
    "why.v1.title": "Délais fixes, zéro surprise",
    "why.v1.desc": "Chaque mission livre dans une fenêtre fixe — pas de facturation horaire ouverte.",
    "why.v2.title": "Résidence des données maîtrisée",
    "why.v2.desc": "Choisissez ÉAU, UE ou Inde. Les contrats précisent exactement où vivent les données.",
    "why.v3.title": "Un partenaire, un contrat",
    "why.v3.desc": "Produit, marketing, cloud, support — tout sous un seul MSA et un seul chef de projet.",
    "why.v4.title": "Résultats mesurables",
    "why.v4.desc": "Chaque projet livre un tableau de bord de KPI — nous nous notons en public.",

    "process.eyebrow": "Notre méthode",
    "process.title": "Un processus de livraison rigoureux en cinq étapes.",
    "process.s1.t": "Découverte",
    "process.s1.d": "Session de travail de 60 min pour cadrer le périmètre, les KPI et les contraintes.",
    "process.s2.t": "Plan directeur",
    "process.s2.d": "Proposition à prix fixe avec jalons, livrables et plan de rollback.",
    "process.s3.t": "Sprint de build",
    "process.s3.d": "Démos hebdomadaires, outil de gestion transparent, accès Slack direct à l'équipe.",
    "process.s4.t": "Mise en ligne",
    "process.s4.d": "Déploiement, migration, formation — puis nous restons à vos côtés 30 jours.",
    "process.s5.t": "Croissance",
    "process.s5.d": "Retainer optionnel pour marketing, cloud ops et itération produit.",

    "success.eyebrow": "Succès mondiaux",
    "success.title": "En production sur trois continents.",
    "success.sub": "Six déploiements représentatifs — de la logistique lagosienne aux académies de Bangalore — montrant le même schéma : problème → ERP taillé → résultat mesurable.",
    "success.problem": "Problème",
    "success.solution": "Solution",
    "success.result": "Résultat",
    "success.disclaimer": "Noms de clients protégés par NDA · références complètes partagées sur demande lors d'une démo.",
    "success.ind.logistics": "Logistique",
    "success.ind.retail": "Retail",
    "success.ind.manufacturing": "Industrie",
    "success.ind.banking": "Banque",
    "success.ind.healthcare": "Santé",
    "success.ind.education": "Éducation",
    "success.ng.p": "Un transporteur transfrontalier perdait 6 % des expéditions/mois à cause de lettres de voiture papier et de routage improvisé.",
    "success.ng.s": "Déploiement de Fleet & Logistics ERP avec réconciliation GPS, capture POD par WhatsApp et WMS multi-entrepôts.",
    "success.ng.r": "livraison à temps 78 → 96 %",
    "success.bw.p": "Chaîne FMCG à 12 points de vente clôturant ses comptes 22 jours après le début du mois avec inventaire manuel.",
    "success.bw.s": "Suite POS + inventaire avec réception code-barres et P&L nocturne au SKU.",
    "success.bw.r": "clôture mois 22 → 4 jours",
    "success.zm.p": "Industriel de la Ceinture de cuivre pilotant son MRP sur Excel partagé — 18 % de gaspillage matière.",
    "success.zm.s": "Manufacturing ERP avec gammes, MRP, portes qualité et conformité e-facture TVA.",
    "success.zm.r": "gaspillage 18 → 6 %",
    "success.zw.p": "Banque privée réconciliant trois devises contre relevés nostro à la main — cycle de 3 jours, correctifs hebdo.",
    "success.zw.s": "Bank Reconciliation Suite avec matching auto Nostro, EFT, cartes et compensation.",
    "success.zw.r": "cycle recon 3 jours → 4 heures",
    "success.ae.p": "Groupe de cliniques 12 branches, taux de no-show 28 %, cycle de règlement TPA 45 jours.",
    "success.ae.s": "HMS Hôpital/Clinique avec rappels à deux étapes, espace TPA et intégration pharmacie.",
    "success.ae.r": "no-show 28 → 18 %",
    "success.in.p": "Chaîne d'académies K-12 ayant perdu 40 % d'engagement app parents après l'abandon d'un vendor legacy.",
    "success.in.s": "School & College ERP avec app parent native, suivi bus live et passerelle de paiement.",
    "success.in.r": "DAU parents × 3,1",

    "contact.eyebrow": "Contact",
    "contact.title": "Réservez une consultation gratuite.",
    "contact.sub": "Un partenaire senior répond sous un jour ouvré. Demandez une démo, un tarif, ou cadrez un pilote — les 20 premières minutes sont offertes.",
    "contact.b1": "Demander une démo",
    "contact.b2": "Voir le tarif",
    "contact.b3": "Lancer un pilote",
    "contact.b4": "Devenir partenaire",
    "contact.whatsapp": "WhatsApp",
    "contact.email": "E-mail",
    "contact.office": "Siège",
    "contact.form.name": "Votre nom",
    "contact.form.email": "E-mail",
    "contact.form.phone": "Téléphone (optionnel)",
    "contact.form.company": "Entreprise",
    "contact.form.msg": "Que construisez-vous ?",
    "contact.form.msg.ph": "Une phrase sur votre projet…",
    "contact.form.wa": "Ouvrir aussi WhatsApp après l'envoi",
    "contact.form.submit": "Envoyer",
    "contact.form.sending": "Envoi…",
    "contact.form.chat": "Discuter sur WhatsApp",
    "contact.toast.error": "Nom, e-mail et message sont obligatoires",
    "contact.toast.success": "Merci — nous vous répondons sous un jour ouvré.",
  },
};

export function translate(lang, key, vars) {
  const raw = (dict[lang] && dict[lang][key]) || dict.en[key] || key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
}
